import i18n from '../i18n';
import { normalizeLocale } from '../i18n/languages';
import { getCalendarLabels } from '../i18n/calendarLabels';
import { getWeekDayLabel } from './dateLocale';

const INTL_LOCALE_MAP = {
  hy: 'hy-AM',
  ka: 'ka-GE',
  kk: 'kk-KZ',
  uz: 'uz-UZ',
  az: 'az-AZ',
};

function activeLang() {
  return normalizeLocale(i18n.resolvedLanguage || i18n.language || 'ru');
}

/** BCP 47 locale for Intl (fallback). */
export function getDateLocale() {
  const code = activeLang();
  return INTL_LOCALE_MAP[code] || code;
}

function parseDateInput(date) {
  if (!date) return null;
  const iso = typeof date === 'string' && date.length === 10 ? `${date}T12:00:00` : date;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isoDayOfWeek(d) {
  return d.getDay() === 0 ? 7 : d.getDay();
}

function monthLabel(d, genitive = false) {
  const labels = getCalendarLabels(activeLang());
  const m = d.getMonth();
  if (genitive && labels.monthsGenitive?.[m]) return labels.monthsGenitive[m];
  if (labels.months?.[m]) return labels.months[m];
  return d.toLocaleDateString(getDateLocale(), { month: 'long' });
}

function weekdayLong(d) {
  const id = isoDayOfWeek(d);
  const fromBundle = getWeekDayLabel(id, 'long');
  if (fromBundle) return fromBundle;
  const labels = getCalendarLabels(activeLang());
  return labels.weekdaysLong?.[id] || d.toLocaleDateString(getDateLocale(), { weekday: 'long' });
}

export function formatDate(date, options = {}) {
  const d = parseDateInput(date);
  if (!d) return '';
  const lang = activeLang();
  const wantsParts = options.weekday || options.month === 'long' || options.month === 'short';

  if (wantsParts) {
    const parts = [];
    if (options.weekday === 'long' || options.weekday === 'short') {
      parts.push(options.weekday === 'long' ? weekdayLong(d) : getWeekDayLabel(isoDayOfWeek(d), 'short') || weekdayLong(d));
    }
    const day = options.day === 'numeric' || options.day != null ? d.getDate() : null;
    const month = options.month === 'long' || options.month === 'short'
      ? monthLabel(d, lang === 'ru' && day != null)
      : null;
    const year = options.year === 'numeric' ? d.getFullYear() : null;
    if (day != null && month) parts.push(`${day} ${month}`);
    else if (day != null) parts.push(String(day));
    if (year != null) parts.push(String(year));
    return parts.join(', ');
  }

  const defaultOptions = { day: 'numeric', month: 'long', year: 'numeric' };
  return d.toLocaleDateString(getDateLocale(), { ...defaultOptions, ...options });
}

export function formatMonth(date) {
  const d = parseDateInput(date instanceof Date ? date : date);
  if (!d) return '';
  const month = monthLabel(d, false);
  return `${month} ${d.getFullYear()}`;
}

export function formatMonthFromParts(year, monthIndex) {
  return formatMonth(new Date(year, monthIndex, 1));
}

export function formatShortDate(date) {
  const d = parseDateInput(date);
  if (!d) return '';
  const labels = getCalendarLabels(activeLang());
  if (labels.months?.[d.getMonth()]) {
    return `${d.getDate()} ${labels.months[d.getMonth()].slice(0, 3)}`;
  }
  return d.toLocaleDateString(getDateLocale(), { day: 'numeric', month: 'short' });
}

export function formatDateTime(date, options = {}) {
  if (!date) return '';
  return new Date(date).toLocaleString(getDateLocale(), options);
}

export function formatTime(date, options = { hour: '2-digit', minute: '2-digit' }) {
  if (!date) return '';
  return new Date(date).toLocaleTimeString(getDateLocale(), options);
}

export function formatWeekdayDate(date) {
  const d = parseDateInput(date);
  if (!d) return '';
  const wd = getWeekDayLabel(isoDayOfWeek(d), 'short') || weekdayLong(d);
  const month = monthLabel(d, activeLang() === 'ru');
  return `${wd}, ${d.getDate()} ${month}`;
}
